/**
 * 团队协作·团队空间（P-D，架构见跨会话记忆「团队协作模块架构定案」）。
 *
 * 全出血双栏：左团队/成员/配额（销售团队版预留）+ 右共享项目（带**签出锁状态演示**）与团队动态。
 * 数据来自 collabProvider（mock）；真同步/权限/租约待接后端。视觉沿用全站一套。
 * 交互要点：无团队时给"创建团队"引导；锁/申请等提示**就近显示在被点卡片下方**（不甩到页面底部）。
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Coins, Crown, HardDrive, Lock, Mail, Plus, Unlock, UserPlus, Users } from 'lucide-react';

import {
  CURRENT_USER_ID,
  ROLE_LABEL,
  canEdit,
  collabProvider,
  lockLabel,
  type Role,
  type SharedProject,
  type Team,
} from '../../model/collab';
import { createEmptyCanvas } from '../../model/canvas';
import { newCanvasId } from '../../features/canvas/canvasLibrary';
import { useCanvasStore } from '../../features/canvas/canvasStore';
import { Modal } from '../../ui/Modal';

function when(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  return new Date(t).toLocaleDateString();
}

function Avatar({ name, size = 24 }: { name: string; size?: number }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-accent/20 font-semibold text-accent"
      style={{ width: size, height: size, fontSize: size * 0.42 }}
      title={name}
    >
      {name.slice(0, 1)}
    </span>
  );
}

function Meter({ used, total, label, Icon }: { used: number; total: number; label: string; Icon: typeof Users }) {
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-uiSm text-ink-muted">
        <Icon size={13} className="text-accent" />
        {label}
        <span className="ml-auto font-num text-ink-secondary">
          {used}/{total}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-base">
        <div className="h-full rounded-full bg-accent/60" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const PLAN_LABEL: Record<Team['plan'], string> = { free: '免费版', team: '团队版', enterprise: '企业版' };

export function TeamSpacePage() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('editor');
  const [createOpen, setCreateOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamPlan, setTeamPlan] = useState<Team['plan']>('team');
  const [notice, setNotice] = useState<{ id: string; text: string } | null>(null);

  useEffect(() => {
    void collabProvider.listTeams().then((ts) => {
      setTeams(ts);
      setTeamId(ts[0]?.id ?? null);
      setLoading(false);
    });
  }, []);

  const team = teams.find((t) => t.id === teamId) ?? null;

  function flash(id: string, text: string): void {
    setNotice({ id, text });
    setTimeout(() => setNotice((cur) => (cur?.id === id ? null : cur)), 6000);
  }

  function openProject(p: SharedProject): void {
    if (!canEdit(p, CURRENT_USER_ID)) {
      flash(p.id, `${p.lock?.userName ?? '他人'} 正在编辑，当前你仅可查看。可点「申请编辑」排队。`);
      return;
    }
    useCanvasStore.getState().setDoc(createEmptyCanvas(newCanvasId(), new Date().toISOString()));
    navigate('/canvas/editor');
  }

  async function requestEdit(p: SharedProject): Promise<void> {
    await collabProvider.requestEdit(p.id);
    flash(p.id, `已向 ${p.lock?.userName ?? '当前编辑者'} 发起「申请编辑」，等待其让出或租约超时释放。`);
  }

  async function sendInvite(): Promise<void> {
    if (email.trim() === '' || team === null) return;
    await collabProvider.invite(team.id, email.trim(), role);
    setTeam({ ...team, usedSeats: team.usedSeats, members: [...team.members] });
    flash('members', `已邀请 ${email.trim()}（${ROLE_LABEL[role]}）——占位，接真后端后邮件生效。`);
    setEmail('');
    setInviteOpen(false);
  }

  function setTeam(next: Team): void {
    setTeams((prev) => prev.map((t) => (t.id === next.id ? next : t)));
  }

  async function createTeam(): Promise<void> {
    if (teamName.trim() === '') return;
    const t = await collabProvider.createTeam(teamName.trim(), teamPlan);
    setTeams((prev) => [t, ...prev]);
    setTeamId(t.id);
    setTeamName('');
    setTeamPlan('team');
    setCreateOpen(false);
  }

  if (loading) {
    return <div className="flex h-full items-center justify-center text-ui text-ink-muted">加载团队空间…</div>;
  }

  // 无团队：创建引导
  if (team === null) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-card bg-accent/15 text-accent">
          <Users size={26} />
        </span>
        <h2 className="text-xl font-semibold">还没有团队</h2>
        <p className="max-w-md text-ui text-ink-muted">创建一个团队，把项目同步到团队云空间，成员即可分工协作、错峰编辑。</p>
        <button type="button" onClick={() => setCreateOpen(true)} className="btn-primary inline-flex items-center gap-1.5 px-4 py-2">
          <Plus size={16} />
          创建团队
        </button>
        <CreateTeamModal open={createOpen} name={teamName} plan={teamPlan} onName={setTeamName} onPlan={setTeamPlan} onCancel={() => setCreateOpen(false)} onConfirm={() => void createTeam()} />
      </div>
    );
  }

  const memberById = new Map(team.members.map((m) => [m.id, m]));

  return (
    <div className="flex h-full overflow-hidden">
      {/* 左：团队 / 成员 / 配额 */}
      <aside className="flex w-[300px] shrink-0 flex-col border-r border-surface-line bg-surface-raised/60">
        <div className="border-b border-surface-line p-4">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-card bg-accent/15 text-accent">
              <Users size={18} />
            </span>
            <div className="min-w-0 flex-1">
              {teams.length > 1 ? (
                <select value={teamId ?? ''} onChange={(e) => setTeamId(e.target.value)} className="w-full truncate bg-transparent text-ui font-semibold text-ink-primary outline-none">
                  {teams.map((t) => (
                    <option key={t.id} value={t.id} className="bg-surface-base">
                      {t.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="truncate text-ui font-semibold">{team.name}</div>
              )}
              <div className="text-uiSm text-ink-faint">{PLAN_LABEL[team.plan]}</div>
            </div>
            <button type="button" onClick={() => setCreateOpen(true)} title="新建团队" aria-label="新建团队" className="shrink-0 rounded-control p-1.5 text-ink-faint transition-colors hover:bg-surface-overlay hover:text-accent">
              <Plus size={16} />
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-b border-surface-line p-4">
          <div className="text-uiSm font-semibold text-ink-faint">配额（销售团队版预留）</div>
          <Meter used={team.usedSeats} total={team.seats} label="席位" Icon={Users} />
          <Meter used={team.usedStorageGB} total={team.storageGB} label="云空间 GB" Icon={HardDrive} />
          <div className="flex items-center gap-1.5 text-uiSm text-ink-muted">
            <Coins size={13} className="text-accent" /> 团队积分池
            <span className="ml-auto font-num text-ink-secondary">{team.creditPool}</span>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-uiSm font-semibold text-ink-faint">成员 · {team.members.length}</span>
            <button type="button" onClick={() => setInviteOpen(true)} className="inline-flex items-center gap-1 text-uiSm text-accent hover:underline">
              <UserPlus size={13} />
              邀请
            </button>
          </div>
          <div className="flex flex-col gap-1.5">
            {team.members.map((m) => (
              <div key={m.id} className="flex items-center gap-2">
                <span className="relative">
                  <Avatar name={m.name} />
                  {m.online && <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-state-ok ring-2 ring-surface-raised" />}
                </span>
                <span className="truncate text-ui text-ink-primary">{m.name}</span>
                {m.role === 'owner' ? (
                  <Crown size={13} className="ml-auto shrink-0 text-accent" />
                ) : (
                  <span className="ml-auto shrink-0 rounded bg-surface-base px-1.5 text-[10px] text-ink-muted">{ROLE_LABEL[m.role]}</span>
                )}
              </div>
            ))}
          </div>
          {notice?.id === 'members' && <p className="mt-2 rounded-control bg-state-info/10 px-2 py-1.5 text-uiSm text-state-info">{notice.text}</p>}
        </div>
      </aside>

      {/* 右：共享项目 + 团队动态 */}
      <main className="flex min-w-0 flex-1 flex-col overflow-y-auto p-6">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-ui font-semibold">共享项目</h2>
        </div>
        <p className="mb-4 text-uiSm text-ink-muted">项目实时同步到团队云空间；某项目有人编辑时对他人**只读锁定**（签出锁·租约），错峰协作不覆盖。</p>
        {team.projects.length === 0 ? (
          <div className="panel mb-8 flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
            <Users size={24} className="text-ink-faint" />
            <p className="text-uiSm text-ink-muted">还没有共享项目。在画布里把项目"分享到团队"后，会出现在这里。</p>
          </div>
        ) : (
          <div className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {team.projects.map((p) => {
              const editable = canEdit(p, CURRENT_USER_ID);
              const locked = !editable;
              return (
                <div key={p.id} className={`panel flex flex-col gap-2 p-4 ${locked ? 'opacity-80' : ''}`}>
                  <div className={`flex h-24 items-center justify-center rounded-control border border-surface-line bg-gradient-to-br ${locked ? 'from-surface-base to-surface-base' : 'from-accent/25 to-surface-base'}`}>
                    {locked ? <Lock size={20} className="text-ink-faint" /> : <Unlock size={20} className="text-accent" />}
                  </div>
                  <h3 className="truncate text-ui font-semibold">{p.name}</h3>
                  <div className={`inline-flex items-center gap-1 text-uiSm ${locked ? 'text-state-warn' : 'text-state-ok'}`}>
                    {locked ? <Lock size={12} /> : <Unlock size={12} />}
                    {lockLabel(p, CURRENT_USER_ID)}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-1.5">
                      {p.memberIds.slice(0, 4).map((id) => (
                        <span key={id} className="rounded-full ring-2 ring-surface-raised">
                          <Avatar name={memberById.get(id)?.name ?? '?'} size={20} />
                        </span>
                      ))}
                    </div>
                    <span className="ml-auto text-uiSm text-ink-faint">{when(p.updatedAt)}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <button type="button" onClick={() => openProject(p)} className={`flex-1 rounded-control px-2 py-1 text-uiSm font-semibold transition-colors ${editable ? 'bg-accent/15 text-accent hover:bg-accent/25' : 'bg-surface-base text-ink-secondary hover:text-ink-primary'}`}>
                      {editable ? '打开编辑' : '只读查看'}
                    </button>
                    {locked && (
                      <button type="button" onClick={() => void requestEdit(p)} className="rounded-control border border-surface-line px-2 py-1 text-uiSm text-ink-secondary hover:text-accent">
                        申请编辑
                      </button>
                    )}
                  </div>
                  {/* 就近内联提示（用户视线在此卡片） */}
                  {notice?.id === p.id && (
                    <p className={`rounded-control px-2 py-1.5 text-uiSm ${locked ? 'bg-state-warn/10 text-state-warn' : 'bg-state-info/10 text-state-info'}`}>{notice.text}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <h2 className="mb-3 flex items-center gap-1.5 text-ui font-semibold">
          <Activity size={15} className="text-accent" />
          团队动态
        </h2>
        <div className="panel flex flex-col divide-y divide-surface-line">
          {team.activity.length === 0 ? (
            <p className="px-4 py-6 text-center text-uiSm text-ink-faint">暂无动态。</p>
          ) : (
            team.activity.map((a) => (
              <div key={a.id} className="flex items-center gap-2 px-4 py-2.5 text-uiSm">
                <Avatar name={a.actor} size={20} />
                <span className="text-ink-primary">
                  <b className="font-semibold">{a.actor}</b> {a.action} <span className="text-accent">{a.target}</span>
                </span>
                <span className="ml-auto shrink-0 text-ink-faint">{when(a.ts)}</span>
              </div>
            ))
          )}
        </div>
      </main>

      {/* 邀请成员 */}
      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="邀请成员"
        summary="按邮箱邀请加入团队并分配角色（占位，接真后端生效）。"
        footer={
          <div className="flex items-center gap-2">
            <button type="button" className="btn-ghost px-3 py-1.5 text-ui" onClick={() => setInviteOpen(false)}>
              取消
            </button>
            <button type="button" className="btn-primary px-3 py-1.5 text-ui" onClick={() => void sendInvite()}>
              发送邀请
            </button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-uiSm text-ink-faint">邮箱</span>
            <div className="flex items-center gap-2 rounded-control bg-surface-base px-2.5">
              <Mail size={14} className="text-ink-faint" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" className="w-full bg-transparent py-2 text-ui text-ink-primary outline-none placeholder:text-ink-faint" />
            </div>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-uiSm text-ink-faint">角色</span>
            <select value={role} onChange={(e) => setRole(e.target.value as Role)} className="rounded-control bg-surface-base px-2.5 py-2 text-ui text-ink-primary outline-none">
              {(['editor', 'viewer', 'commenter'] as Role[]).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Modal>

      <CreateTeamModal open={createOpen} name={teamName} plan={teamPlan} onName={setTeamName} onPlan={setTeamPlan} onCancel={() => setCreateOpen(false)} onConfirm={() => void createTeam()} />
    </div>
  );
}

function CreateTeamModal(props: {
  open: boolean;
  name: string;
  plan: Team['plan'];
  onName: (v: string) => void;
  onPlan: (v: Team['plan']) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open={props.open}
      onClose={props.onCancel}
      title="创建团队"
      summary="团队成员在专属云空间共享项目、分工协作。"
      footer={
        <div className="flex items-center gap-2">
          <button type="button" className="btn-ghost px-3 py-1.5 text-ui" onClick={props.onCancel}>
            取消
          </button>
          <button type="button" className="btn-primary px-3 py-1.5 text-ui" onClick={props.onConfirm}>
            创建
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-uiSm text-ink-faint">团队名称</span>
          <input value={props.name} onChange={(e) => props.onName(e.target.value)} placeholder="如：星辰短剧组" className="rounded-control bg-surface-base px-2.5 py-2 text-ui text-ink-primary outline-none placeholder:text-ink-faint" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-uiSm text-ink-faint">套餐</span>
          <select value={props.plan} onChange={(e) => props.onPlan(e.target.value as Team['plan'])} className="rounded-control bg-surface-base px-2.5 py-2 text-ui text-ink-primary outline-none">
            {(['free', 'team', 'enterprise'] as Team['plan'][]).map((p) => (
              <option key={p} value={p}>
                {PLAN_LABEL[p]}
              </option>
            ))}
          </select>
        </label>
      </div>
    </Modal>
  );
}
